import React, { useState, useEffect, useCallback } from 'react';
import { Eye, Search, X } from 'lucide-react';
import api from '../../services/api';

interface LogAcesso {
  id: number;
  usuario_id?: number;
  usuario_nome?: string;
  usuario_email?: string;
  acao?: string;
  metodo?: string;
  recurso?: string;
  secao?: string;
  descricao?: string;
  ip_address?: string;
  status_code?: number;
  id_creche?: number;
  dados?: unknown;
  criado_em?: string;
}

interface RespostaAuditoria {
  data: LogAcesso[];
  total: number;
  page: number;
  limit: number;
}

// Seções (módulos) mais comuns — filtra pelo prefixo da rota. A busca livre cobre o resto.
const SECOES: Array<{ v: string; l: string }> = [
  { v: '', l: 'Todas as seções' },
  { v: 'saas', l: 'SaaS / Tenants' },
  { v: 'creches', l: 'Creches' },
  { v: 'alunos', l: 'Alunos' },
  { v: 'turmas', l: 'Turmas' },
  { v: 'matriculas', l: 'Matrículas' },
  { v: 'academico', l: 'Acadêmico / Filas' },
  { v: 'financeiro', l: 'Financeiro' },
  { v: 'funcionarios', l: 'Funcionários' },
  { v: 'familias', l: 'Famílias' },
  { v: 'pedagogico', l: 'Pedagógico' },
  { v: 'rotina', l: 'Rotina' },
  { v: 'secretaria', l: 'Secretaria' },
  { v: 'auth', l: 'Autenticação' },
  { v: 'lgpd', l: 'LGPD' },
  { v: 'ia', l: 'IA / Liz' },
  { v: 'publico', l: 'Público' },
];

const FILTROS_VAZIOS = { busca: '', secao: '', tenant: '', acao: '', de: '', ate: '' };
const LIMITE = 50;

export default function AuditoriaPage() {
  const [logs, setLogs] = useState<LogAcesso[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [carregando, setCarregando] = useState(true);
  const [selectedLog, setSelectedLog] = useState<LogAcesso | null>(null);

  // Filtro de texto separado (com debounce) dos demais filtros imediatos.
  const [buscaInput, setBuscaInput] = useState('');
  const [filtros, setFiltros] = useState({ ...FILTROS_VAZIOS });

  // Debounce da busca livre (evita uma consulta por tecla).
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltros((f) => ({ ...f, busca: buscaInput }));
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [buscaInput]);

  const carregarDados = useCallback(async () => {
    setCarregando(true);
    try {
      const params: Record<string, string | number> = { page, limit: LIMITE };
      if (filtros.busca) params.busca = filtros.busca;
      if (filtros.secao) params.secao = filtros.secao;
      if (filtros.tenant) params.tenant = filtros.tenant;
      if (filtros.acao) params.acao = filtros.acao;
      if (filtros.de) params.de = filtros.de;
      if (filtros.ate) params.ate = filtros.ate;

      const resp = await api.get<RespostaAuditoria | LogAcesso[]>('/lgpd/auditoria', { params });
      const body: any = resp.data;
      if (Array.isArray(body)) {
        setLogs(body);
        setTotal(body.length);
      } else {
        setLogs(body?.data ?? []);
        setTotal(body?.total ?? 0);
      }
    } finally {
      setCarregando(false);
    }
  }, [page, filtros]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  function alterarFiltro(campo: keyof typeof FILTROS_VAZIOS, valor: string) {
    setFiltros((f) => ({ ...f, [campo]: valor }));
    setPage(1);
  }

  function limparFiltros() {
    setBuscaInput('');
    setFiltros({ ...FILTROS_VAZIOS });
    setPage(1);
  }

  const totalPaginas = Math.max(1, Math.ceil(total / LIMITE));
  const temFiltroAtivo = buscaInput !== '' || Object.values(filtros).some((v) => v !== '');

  function fmtData(iso?: string) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR');
  }

  // Localiza o registro ANTERIOR do mesmo recurso (edição imediatamente anterior),
  // dentro da lista já carregada, para comparar o que mudou nesta ação.
  function encontrarAnterior(alvo: LogAcesso): LogAcesso | null {
    if (!alvo?.recurso || !alvo?.criado_em) return null;
    const alvoTime = new Date(alvo.criado_em).getTime();
    const candidatos = logs
      .filter((l) =>
        l.id !== alvo.id &&
        l.recurso === alvo.recurso &&
        l.dados && typeof l.dados === 'object' &&
        l.criado_em && new Date(l.criado_em).getTime() < alvoTime,
      )
      .sort((a, b) => new Date(b.criado_em!).getTime() - new Date(a.criado_em!).getTime());
    return candidatos[0] ?? null;
  }

  // Retorna o conjunto de chaves cujos valores diferem entre dois payloads.
  function camposDiferentes(atual: Record<string, unknown>, anterior: Record<string, unknown>): Set<string> {
    const set = new Set<string>();
    const chaves = Array.from(new Set([...Object.keys(atual), ...Object.keys(anterior)]));
    for (const k of chaves) {
      if (JSON.stringify(atual[k]) !== JSON.stringify(anterior[k])) set.add(k);
    }
    return set;
  }

  const inputCls = 'px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500';

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-2xl p-8 text-white shadow-lg">
        <h1 className="text-4xl font-bold mb-2">Auditoria Global</h1>
        <p className="text-red-100">Acompanhamento de todas as ações realizadas no SaaS</p>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-green-200">
          <p className="text-sm font-medium text-gray-600 mb-2">Status de Segurança</p>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 bg-green-500 rounded-full"></div>
            <span className="text-lg font-bold text-green-600">Excelente</span>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-blue-200">
          <p className="text-sm font-medium text-gray-600 mb-2">Eventos {temFiltroAtivo ? 'no filtro' : 'registrados'}</p>
          <p className="text-2xl font-bold text-blue-600">{total}</p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-200">
          <p className="text-sm font-medium text-gray-600 mb-2">Página</p>
          <p className="text-2xl font-bold text-orange-600">{page} <span className="text-base text-gray-400">de {totalPaginas}</span></p>
        </div>
      </div>

      {/* Barra de filtros */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Busca livre (rota, usuário, e-mail)</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={buscaInput}
                onChange={(e) => setBuscaInput(e.target.value)}
                placeholder="Ex: tenants/6, editar, joão..."
                className={`${inputCls} w-full pl-9`}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Seção</label>
            <select value={filtros.secao} onChange={(e) => alterarFiltro('secao', e.target.value)} className={inputCls}>
              {SECOES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Tenant (id da creche)</label>
            <input
              type="number"
              value={filtros.tenant}
              onChange={(e) => alterarFiltro('tenant', e.target.value)}
              placeholder="Ex: 6"
              className={`${inputCls} w-32`}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Ação</label>
            <select value={filtros.acao} onChange={(e) => alterarFiltro('acao', e.target.value)} className={inputCls}>
              <option value="">Todas</option>
              <option value="criar">Criações</option>
              <option value="editar">Edições</option>
              <option value="excluir">Exclusões</option>
              <option value="acessar">Acessos</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">De</label>
            <input type="date" value={filtros.de} onChange={(e) => alterarFiltro('de', e.target.value)} className={inputCls} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Até</label>
            <input type="date" value={filtros.ate} onChange={(e) => alterarFiltro('ate', e.target.value)} className={inputCls} />
          </div>

          {temFiltroAtivo && (
            <button
              onClick={limparFiltros}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-1 text-sm"
            >
              <X size={16} /> Limpar
            </button>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Eventos de Auditoria</h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Data/Hora</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Usuário</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Seção</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Ação</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Recurso</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">IP</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700"></th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr><td colSpan={7} className="py-10 text-center text-gray-500">Carregando…</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={7} className="py-8 text-center text-gray-500">Nenhum evento encontrado</td></tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-900 whitespace-nowrap">{fmtData(log.criado_em)}</td>
                    <td className="py-3 px-4 text-gray-600">
                      {log.usuario_nome || (log.usuario_id ? `Usuário #${log.usuario_id}` : 'Sistema/Público')}
                      {log.usuario_email && <span className="block text-xs text-gray-400">{log.usuario_email}</span>}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">{log.secao || '—'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">{log.acao || '—'}</span>
                    </td>
                    <td className="py-3 px-4 text-gray-600 font-mono text-xs break-all">
                      {log.metodo ? `${log.metodo} ` : ''}{log.recurso || log.descricao || '—'}
                    </td>
                    <td className="py-3 px-4 text-gray-600 font-mono text-xs">{log.ip_address || '—'}</td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => setSelectedLog(log)}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex items-center gap-1"
                      >
                        <Eye size={14} /> Ver
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-gray-500">
            {total > 0 ? `${(page - 1) * LIMITE + 1}–${Math.min(page * LIMITE, total)} de ${total}` : '0 registros'}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || carregando}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
            >
              ← Anterior
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
              disabled={page >= totalPaginas || carregando}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
            >
              Próxima →
            </button>
          </div>
        </div>
      </div>

      {/* Modal Detalhes */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedLog(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Detalhes do Evento</h3>

            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase">Data/Hora</p>
                <p className="text-gray-900">{fmtData(selectedLog.criado_em)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase">Usuário</p>
                <p className="text-gray-900">
                  {selectedLog.usuario_nome || (selectedLog.usuario_id ? `#${selectedLog.usuario_id}` : 'Sistema/Público')}
                  {selectedLog.usuario_email ? ` · ${selectedLog.usuario_email}` : ''}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase">Ação</p>
                <p className="text-gray-900">{selectedLog.acao || '—'}{selectedLog.status_code ? ` · status ${selectedLog.status_code}` : ''}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase">Recurso</p>
                <p className="text-gray-900 font-mono text-xs break-all">
                  {selectedLog.metodo ? `${selectedLog.metodo} ` : ''}{selectedLog.recurso || selectedLog.descricao || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase">IP Address</p>
                <p className="text-gray-900 font-mono">{selectedLog.ip_address || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase">Dados Alterados (Sanitizado)</p>
                {selectedLog.dados && typeof selectedLog.dados === 'object' ? (() => {
                  const dados = selectedLog.dados as Record<string, unknown>;
                  const anterior = encontrarAnterior(selectedLog);
                  const dadosAnterior = (anterior?.dados && typeof anterior.dados === 'object')
                    ? (anterior.dados as Record<string, unknown>)
                    : null;
                  const alterados = dadosAnterior ? camposDiferentes(dados, dadosAnterior) : null;
                  return (
                    <div className="space-y-2">
                      {anterior ? (
                        <div className="p-2 bg-green-50 border border-green-300 rounded text-xs text-green-800">
                          Comparado com o registro anterior deste recurso ({fmtData(anterior.criado_em)}). ✏️ = campo alterado nesta ação.
                        </div>
                      ) : (
                        <div className="p-2 bg-gray-50 border border-gray-300 rounded text-xs text-gray-600">
                          Sem registro anterior deste recurso na lista carregada — exibindo os valores enviados, sem comparação.
                        </div>
                      )}
                      <table className="w-full text-xs border-collapse border border-gray-300 rounded-lg overflow-hidden">
                        <tbody>
                          {Object.entries(dados).map(([chave, valor]) => {
                            const isMasked = String(valor).startsWith('***');
                            const mudou = alterados?.has(chave) ?? false;
                            const valorStr = typeof valor === 'string' ? valor : JSON.stringify(valor);
                            const antes = dadosAnterior ? dadosAnterior[chave] : undefined;
                            const antesStr = antes === undefined ? undefined : (typeof antes === 'string' ? antes : JSON.stringify(antes));
                            if (mudou) {
                              return (
                                <tr key={chave} className="bg-green-100 border-l-4 border-l-green-600">
                                  <td className="p-2 border-b border-green-300 font-bold text-green-800 w-1/3">✏️ {chave}</td>
                                  <td className="p-2 border-b border-green-300 font-mono">
                                    {antesStr !== undefined && antesStr !== valorStr && (
                                      <span className="text-red-500 line-through mr-2">{antesStr}</span>
                                    )}
                                    <span className={`font-bold ${isMasked ? 'text-red-700' : 'text-green-700'}`}>{valorStr}</span>
                                  </td>
                                </tr>
                              );
                            }
                            return (
                              <tr key={chave} className="bg-gray-50">
                                <td className="p-2 border-b border-gray-200 text-gray-500 w-1/3">{chave}</td>
                                <td className={`p-2 border-b border-gray-200 font-mono text-gray-600 ${isMasked ? 'text-red-600' : ''}`}>
                                  {valorStr}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                        <strong>Legenda:</strong> ✏️ verde = alterado nesta ação (mostra <span className="line-through">antes</span> → depois) · 🔴 vermelho = dado mascarado (***NNNN) · cinza = inalterado
                      </div>
                    </div>
                  );
                })() : selectedLog.dados ? (
                  <pre className="text-gray-900 text-xs bg-gray-50 border-2 border-gray-300 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(selectedLog.dados, null, 2)}
                  </pre>
                ) : (
                  <p className="text-gray-500 text-xs">— (leitura, sem alteração de dados)</p>
                )}
              </div>
            </div>

            <button
              onClick={() => setSelectedLog(null)}
              className="mt-6 w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
